import { API_ENDPOINTS } from '@/shared/apiClient';
import { authService } from '@/features/auth/services/authService';
import type { VisibilityTier } from '@/shared/types/projectData';

export type ApprovalRequestScope = 'node' | 'subtree' | 'project' | 'project-rewrite';
export type ApprovalRequestStatus = 'pending' | 'approved' | 'rejected' | 'executed' | 'execution_failed';

export interface ApprovalRequestRecord {
  id: string;
  project_id: string;
  project_name?: string | null;
  node_id?: string | null;
  node_type?: string | null;
  node_title?: string | null;
  scope: ApprovalRequestScope;
  current_visibility_tier: VisibilityTier;
  requested_visibility_tier: VisibilityTier;
  requester_user_id: string;
  requester_username?: string | null;
  reviewer_user_id?: string | null;
  reviewer_username?: string | null;
  rationale: string;
  status: ApprovalRequestStatus;
  execution_error?: string | null;
  created_at: string;
  reviewed_at?: string | null;
  executed_at?: string | null;
}

interface ApprovalRequestResponse {
  success: boolean;
  data?: {
    approvalRequest?: ApprovalRequestRecord;
  };
}

/** A refused approval-request call. `status` lets callers tell "already decided" (400/404) from "snapshot changed" (409). */
export class ApprovalRequestServiceError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApprovalRequestServiceError';
    this.status = status;
  }
}

const refusal = async (response: Response, fallback: string): Promise<ApprovalRequestServiceError> => {
  const errorPayload = await response.json().catch(() => null);
  return new ApprovalRequestServiceError(
    typeof errorPayload?.detail === 'string' && errorPayload.detail ? errorPayload.detail : fallback,
    response.status,
  );
};

export const approvalRequestService = {
  async create(payload: {
    projectId: string;
    nodeId?: string | null;
    scope: ApprovalRequestScope;
    requestedVisibilityTier: VisibilityTier;
    rationale: string;
  }): Promise<ApprovalRequestRecord> {
    const response = await authService.authenticatedFetch(API_ENDPOINTS.approvalRequests.create, {
      method: 'POST',
      body: JSON.stringify({
        project_id: payload.projectId,
        node_id: payload.nodeId || null,
        scope: payload.scope,
        requested_visibility_tier: payload.requestedVisibilityTier,
        rationale: payload.rationale,
      }),
    });
    if (!response.ok) {
      throw await refusal(response, 'Failed to create approval request');
    }
    const body = (await response.json()) as ApprovalRequestResponse;
    if (!body.data?.approvalRequest) {
      throw new Error('Approval request payload missing');
    }
    return body.data.approvalRequest;
  },

  async approve(approvalRequestId: string, reviewNote?: string): Promise<ApprovalRequestRecord> {
    const response = await authService.authenticatedFetch(
      API_ENDPOINTS.approvalRequests.approve(approvalRequestId),
      {
        method: 'POST',
        body: JSON.stringify({ review_note: reviewNote || null }),
      },
    );
    if (!response.ok) {
      throw await refusal(response, 'Failed to approve request');
    }
    const body = (await response.json()) as ApprovalRequestResponse;
    if (!body.data?.approvalRequest) {
      throw new Error('Approval response payload missing');
    }
    return body.data.approvalRequest;
  },

  async reject(approvalRequestId: string, reviewNote?: string): Promise<ApprovalRequestRecord> {
    const response = await authService.authenticatedFetch(
      API_ENDPOINTS.approvalRequests.reject(approvalRequestId),
      {
        method: 'POST',
        body: JSON.stringify({ review_note: reviewNote || null }),
      },
    );
    if (!response.ok) {
      throw await refusal(response, 'Failed to reject request');
    }
    const body = (await response.json()) as ApprovalRequestResponse;
    if (!body.data?.approvalRequest) {
      throw new Error('Approval response payload missing');
    }
    return body.data.approvalRequest;
  },
};
